import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, X, Stethoscope } from 'lucide-react';

const History = () => {
  const [history, setHistory] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ diagnosis: '', treatment: '', notes: '' });

  useEffect(() => {
    fetchAppointments();
    fetchHistory();
  }, []);

  const fetchAppointments = () => {
    fetch('/api/appointments')
      .then(res => res.json())
      .then(data => setAppointments(data));
  };

  const fetchHistory = () => {
    setHistory([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedAppointment) return;
    fetch(`/api/appointments/${selectedAppointment}/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    }).then(() => {
      fetchHistory();
      setShowForm(false);
      setFormData({ diagnosis: '', treatment: '', notes: '' });
      setSelectedAppointment('');
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ diagnosis: '', treatment: '', notes: '' });
    setSelectedAppointment('');
  };

  const filteredHistory = history.filter(record =>
    record.patient_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">История посещений</h1>
          <p className="text-slate-500 mt-0.5">Записи о приёмах и диагнозах</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Добавить запись</span>
        </button>
      </div>

      {/* Поиск */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Поиск по имени пациента..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      {/* История */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">История посещений</h2>
        </div>

        {filteredHistory.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">История посещений пуста</p>
            <p className="text-sm mt-1">Добавьте первую запись в историю</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredHistory.map((record) => (
              <div key={record.id} className="px-5 py-5">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Stethoscope className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-slate-900">{record.patient_name}</h3>
                      <span className="text-sm text-slate-400">{record.date}</span>
                    </div>
                    <p className="text-slate-800 font-semibold">{record.diagnosis}</p>
                    {record.treatment && (
                      <p className="text-slate-600 mt-1 text-sm">
                        <span className="font-medium">Лечение:</span> {record.treatment}
                      </p>
                    )}
                    {record.notes && (
                      <p className="text-slate-500 mt-1 text-sm">
                        <span className="font-medium">Заметки:</span> {record.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-y-auto max-h-[92vh] shadow-2xl">
            <div className="bg-blue-800 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Добавить запись в историю</h3>
              <button onClick={handleCancel} className="text-blue-300 hover:text-white p-1 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Выбрать запись *</label>
                <select
                  value={selectedAppointment}
                  onChange={(e) => setSelectedAppointment(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                >
                  <option value="">Выберите завершённую запись...</option>
                  {appointments.filter(apt => apt.status === 'completed').map(apt => (
                    <option key={apt.id} value={apt.id}>
                      {apt.patient_name} — {apt.date} {apt.time}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Диагноз *</label>
                <input
                  type="text"
                  value={formData.diagnosis}
                  onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Поставленный диагноз"
                  required
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Лечение</label>
                <textarea
                  value={formData.treatment}
                  onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  rows="3"
                  placeholder="Назначенное лечение..."
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Заметки</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  rows="3"
                  placeholder="Дополнительные заметки..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-3.5 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
                >
                  Добавить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
